import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

export default function EventStatusManager({
  eventStatuses,
  setEventStatuses,
}) {
  const [newStatus, setNewStatus] = useState('')
  const [newColor, setNewColor] = useState('#1976d2')

  const handleAddStatus = () => {
    const trimmed = newStatus.trim()
    if (!trimmed) return

    const alreadyExists = Object.keys(eventStatuses).some(
      (status) => status.toLowerCase() === trimmed.toLowerCase(),
    )

    if (alreadyExists) return

    setEventStatuses((prev) => ({
      ...prev,
      [trimmed]: newColor,
    }))

    setNewStatus('')
    setNewColor('#1976d2')
  }

  const handleDeleteStatus = (labelToDelete) => {
    setEventStatuses((prev) => {
      const updated = { ...prev }
      delete updated[labelToDelete]
      return updated
    })
  }

  const handleColorChange = (labelToUpdate, color) => {
    setEventStatuses((prev) => ({
      ...prev,
      [labelToUpdate]: color,
    }))
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Event Statuses
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add, edit, and remove the statuses available for events.
        </Typography>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              label="New status"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              fullWidth
            />

            <TextField
              label="Colour"
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              sx={{ width: { xs: '100%', md: 120 } }}
              InputLabelProps={{ shrink: true }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddStatus}
              sx={{ minWidth: 120 }}
            >
              Add
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <List disablePadding>
          {Object.entries(eventStatuses).map(([label, color], index, arr) => (
            <Box key={label}>
              <ListItem sx={{ py: 1.5, px: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  useFlexGap
                  // Wrap the colour/delete controls under the chip on narrow
                  // screens instead of pushing past the viewport edge
                  sx={{ width: '100%', flexWrap: 'wrap', rowGap: 1 }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                      label={label}
                      sx={{
                        backgroundColor: color,
                        color: '#fff',
                        fontWeight: 500,
                        minWidth: 120,
                      }}
                    />

                    <Typography variant="body1">{label}</Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      type="color"
                      value={color.startsWith('#') ? color : '#1976d2'}
                      onChange={(e) => handleColorChange(label, e.target.value)}
                      sx={{ width: 72 }}
                    />
                      <IconButton
                        onClick={() => handleDeleteStatus(label)}
                        color="error"
                        disabled={['Planning', 'Ready', 'Cancelled'].includes(label)}
                      >
                        <DeleteIcon />
                      </IconButton>
                  </Stack>
                </Stack>
              </ListItem>

              {index < arr.length - 1 && <Divider />}
            </Box>
          ))}
        </List>
      </Card>
    </Stack>
  )
}
